import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to fill the container width. */
  block?: boolean;
  /** Optional leading glyph / icon. */
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const base =
  "relative inline-flex items-center justify-center gap-2 font-display font-bold " +
  "whitespace-nowrap select-none rounded-pill transition-all duration-fast ease-kinetic " +
  "active:translate-y-px disabled:pointer-events-none disabled:opacity-50 " +
  "focus-visible:outline-none";

const sizes: Record<ButtonSize, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-12 px-6 text-base",
  lg: "h-14 px-8 text-lg",
};

const variants: Record<ButtonVariant, string> = {
  // Pulse signal CTA — coral→orange gradient with a coral glow.
  primary:
    "bg-grad-coral text-white shadow-volt hover:shadow-volt-lg " +
    "hover:-translate-y-0.5 hover:saturate-[1.06] hover:brightness-[1.03] tracking-tight",
  // Violet gradient pill for confident secondary actions.
  secondary:
    "bg-grad-violet text-white shadow-ember hover:shadow-volt-lg " +
    "hover:-translate-y-0.5 hover:saturate-[1.06] hover:brightness-[1.03] tracking-tight",
  // Quiet, white pill with a hairline + soft lift.
  ghost:
    "bg-surface text-body-muted ring-1 ring-inset ring-line shadow-xs " +
    "hover:bg-surface-sunken hover:text-body",
  // Destructive — clear, not shouty.
  destructive:
    "bg-danger text-white shadow-volt hover:brightness-110 hover:-translate-y-0.5",
};

/**
 * The primary interactive element. Four variants × three sizes, all
 * keyboard-focusable with the shared Pulse coral focus ring, motion-reduced
 * safe (transitions collapse under prefers-reduced-motion via global.css).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      block = false,
      iconLeft,
      iconRight,
      className,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        base,
        sizes[size],
        variants[variant],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {iconLeft && <span className="-ml-0.5 inline-flex shrink-0">{iconLeft}</span>}
      {children}
      {iconRight && <span className="-mr-0.5 inline-flex shrink-0">{iconRight}</span>}
    </button>
  ),
);

Button.displayName = "Button";
